// ---------------------------------------------------------------------------
// UserInTheLoop Admin SDK — API Client
// ---------------------------------------------------------------------------

import type {
  UITLConfig,
  SDKConfigResponse,
  ChatSession,
  FeedbackSubmission,
  Announcement,
  GuardrailResult,
  GuardrailRule,
  FeatureToggle,
  KBArticle,
} from './types';

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class UITLApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'UITLApiError';
  }
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export class UITLApiClient {
  private apiKey: string;
  private baseUrl: string;
  private siteDomain: string;
  private user?: { id?: string; email?: string; role?: string };

  constructor(config: UITLConfig) {
    this.apiKey = config.apiKey;
    this.baseUrl = (config.baseUrl ?? 'https://api.patchwise.ai').replace(
      /\/$/,
      '',
    );

    this.siteDomain =
      config.siteDomain ??
      (typeof window !== 'undefined' ? window.location.hostname : 'unknown');

    if (config.user) {
      this.user = {
        id: config.user.id,
        email: config.user.email,
        role: config.user.role,
      };
    }
  }

  // -----------------------------------------------------------------------
  // Internal helpers
  // -----------------------------------------------------------------------

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const url = `${this.baseUrl}/api/v1/sdk${path}`;

    const headers: Record<string, string> = {
      'X-API-Key': this.apiKey,
      'X-UITL-Site': this.siteDomain,
      'Content-Type': 'application/json',
    };

    if (this.user) {
      headers['X-UITL-User'] = JSON.stringify(this.user);
    }

    const res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let errorBody: unknown;
      try {
        errorBody = await res.json();
      } catch {
        errorBody = await res.text().catch(() => null);
      }
      throw new UITLApiError(
        `UITL API error ${res.status}: ${res.statusText}`,
        res.status,
        errorBody,
      );
    }

    // 204 No Content — nothing to parse
    if (res.status === 204) {
      return undefined as unknown as T;
    }

    return res.json() as Promise<T>;
  }

  /** Update the user context after initialisation. */
  setUser(user: { id?: string; email?: string; role?: string }): void {
    this.user = user;
  }

  // -----------------------------------------------------------------------
  // Config
  // -----------------------------------------------------------------------

  /** Fetch remote SDK configuration for this site. */
  async getConfig(): Promise<SDKConfigResponse> {
    return this.request<SDKConfigResponse>('GET', '/config');
  }

  // -----------------------------------------------------------------------
  // Chat
  // -----------------------------------------------------------------------

  /** Create a new chat session, optionally specifying a persona mode. */
  async createChatSession(
    mode?: string,
  ): Promise<ChatSession> {
    return this.request<ChatSession>('POST', '/chat/sessions', {
      ...(mode ? { mode } : {}),
    });
  }

  /** Send a message in an existing chat session and get the reply. */
  async sendChatMessage(
    sessionId: number,
    message: string,
  ): Promise<{ reply: string; session_id: number }> {
    return this.request<{ reply: string; session_id: number }>(
      'POST',
      `/chat/sessions/${sessionId}/messages`,
      { message },
    );
  }

  // -----------------------------------------------------------------------
  // Feedback
  // -----------------------------------------------------------------------

  /** Submit user feedback (bug, feature request, or general feedback). */
  async submitFeedback(
    data: FeedbackSubmission,
  ): Promise<{ id: number; status: string }> {
    return this.request<{ id: number; status: string }>(
      'POST',
      '/feedback',
      data,
    );
  }

  // -----------------------------------------------------------------------
  // Announcements
  // -----------------------------------------------------------------------

  /** List published announcements for this site. */
  async getAnnouncements(): Promise<Announcement[]> {
    return this.request<Announcement[]>('GET', '/announcements');
  }

  /** Mark a specific announcement as read by the current user. */
  async markAnnouncementRead(id: number): Promise<void> {
    return this.request<void>('POST', `/announcements/${id}/read`);
  }

  /** Get the count of unread announcements for the current user. */
  async getUnreadCount(): Promise<number> {
    const res = await this.request<{ count: number }>(
      'GET',
      '/announcements/unread-count',
    );
    return res.count;
  }

  // -----------------------------------------------------------------------
  // Guardrails
  // -----------------------------------------------------------------------

  /** Validate arbitrary content against the site's guardrail rules. */
  async validateContent(
    content: string,
    contentRef?: string,
  ): Promise<GuardrailResult> {
    return this.request<GuardrailResult>('POST', '/guardrails/validate', {
      content,
      ...(contentRef ? { content_ref: contentRef } : {}),
    });
  }

  /** List all guardrail rules configured for this site. */
  async getGuardrails(): Promise<GuardrailRule[]> {
    return this.request<GuardrailRule[]>('GET', '/guardrails');
  }

  // -----------------------------------------------------------------------
  // Feature toggles
  // -----------------------------------------------------------------------

  /** Retrieve the feature-toggle map for this site. */
  async getFeatureToggles(): Promise<FeatureToggle> {
    return this.request<FeatureToggle>('GET', '/features');
  }

  // -----------------------------------------------------------------------
  // Knowledge base
  // -----------------------------------------------------------------------

  /** Search or list knowledge-base articles. */
  async searchKBArticles(query?: string): Promise<KBArticle[]> {
    const qs = query ? `?q=${encodeURIComponent(query)}` : '';
    return this.request<KBArticle[]>('GET', `/kb/articles${qs}`);
  }
}
